import React, { useState, useRef, useEffect } from 'react';
import i18next from 'i18next';
import clsx from 'clsx';

import { Dropdown } from 'primereact/dropdown';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { Tooltip } from 'primereact/tooltip';
import { ConfirmPopup, confirmPopup } from 'primereact/confirmpopup';

import useSelectedCompanyStore from '../store/useSelectedCompanyStore';
import useUserStore from '../store/useUserStore';
import useTokenStore from '../store/useTokenStore';

import useIsMobile from '../../test/utils/useIsMobile';
import NotificationPanel from './layoutComponents/NotificationPanel';
import GeneralQuery from './layoutComponents/GeneralQuery';

const t = (i18nKey, params) => i18next.t(`userDashboard.header.${i18nKey}`, params);

const Header = ({ hasNotification = false, isCollapsed, setIsCollapsed }) => {
    const [currentLanguage, setCurrentLanguage] = useState(window.locale || 'fr');
    const [searchResults, setSearchResults] = useState(null);

    const userPanelRef = useRef(null);

    const selectedCompany = useSelectedCompanyStore((state) => state.selectedCompany);

    const user = useUserStore((state) => state.user);

    const clearUser = useUserStore((state) => state.clearUser);
    const clearToken = useTokenStore((state) => state.clearToken);

    const [credits, setCredits] = useState(selectedCompany?.credits || 0);
    const [creditsChanging, setCreditsChanging] = useState(false);

    const isMobile = useIsMobile(768, false);

    useEffect(() => {
        if (selectedCompany?.credits !== undefined) {
            const targetCredits = selectedCompany.credits;

            const updateCredits = () => {
                setCreditsChanging(true);
                setCredits(prevCredits => {
                    if (prevCredits < targetCredits) {
                        return prevCredits + 1;
                    } else if (prevCredits > targetCredits) {
                        return prevCredits - 1;
                    } else {
                        clearInterval(intervalId);
                        setCreditsChanging(false);
                        return prevCredits;
                    }
                });
            };

            const intervalId = setInterval(updateCredits, 100);

            return () => {
                clearInterval(intervalId);
                setCreditsChanging(false);
            };
        }
    }, [selectedCompany?.credits]);

    const handleDisconnect = () => {
        clearUser();
        clearToken();
        sessionStorage.removeItem('lastActivity');

        // TODO: Make an actual request to the back-end to disconnect, that way even with the same token i'm disconnected.

        window.location.reload();
    };

    const handleLanguageChange = (e) => {
        setCurrentLanguage(e.value);
        window.location.href = `/language/${e.value}`;
    };

    const accept = () => {
        window.location.href = window.origin;
    };

    const handleLogoClick = (event) => {
        confirmPopup({
            target: event.currentTarget,
            header: 'Confirmation',
            message: (
                <div className="is-flex">
                    ⚠️ <p className='ml-1'>Retour au site principal ? ({window.location.hostname})</p>
                </div>
            ),
            defaultFocus: 'reject',
            acceptClassName: 'button is-warning',
            rejectClassName: 'button is-secondary mr-2',
            acceptLabel: 'Oui',
            rejectLabel: 'Non, rester',
            accept
        });
    };

    const handleSearchResults = (results) => {
        setSearchResults(results);
    };

    return (
        <>
            <Tooltip target=".disabledInput" mouseTrack mouseTrackTop={10} mouseTrackLeft={0} />

            <header className="header">
                <div className={clsx('header-container', { 'has-notification': hasNotification })}>

                    {isMobile && (
                        <div
                            className={clsx('sidebar-icon')}
                            onClick={() => setIsCollapsed(!isCollapsed)}>
                            <i className={clsx({ 'fas fa-bars': isCollapsed, 'fas fa-times': !isCollapsed })}></i>
                        </div>
                    )}

                    <ConfirmPopup />
                    <a className="navbar-item ml-0 mr-auto" onClick={handleLogoClick}>
                        <div className="logo-slogan text-is-unselectable">
                            <img className="is-block mr-auto logo text-is-unselectable" src="/images/svg/logo-deepsy-svg.svg" alt="DeePsy logo" />
                            <img src="/images/svg/slogan-deepsy.svg" className="is-hidden-mobile ml-3 text-is-unselectable" alt="DeePsy slogan" />
                        </div>
                    </a>

                    <GeneralQuery onResults={handleSearchResults} />

                    <div className="credits ml-1">
                        <button
                            className={`button ${creditsChanging ? 'changing' : ''}`}
                            onClick={() => { (window.location.pathname + window.location.search) !== '/dashboard/billing?tab=addCredits' ? window.location.href = '/dashboard/billing?tab=addCredits' : '' }}
                        >
                            Credits : {credits || 0} <i className="fa-solid fa-circle-plus ml-2"></i>
                        </button>
                    </div>

                    <div className="language-selector">
                        <div className="navbar-item is-hoverable">
                            <div className="language-selector">
                                <Dropdown
                                    value={currentLanguage}
                                    options={[
                                        { label: 'FR', value: 'fr' },
                                        { label: 'EN', value: 'en' }
                                    ]}
                                    onChange={handleLanguageChange}
                                    className='primereact-dropdown-conventional primereact-dropdown-less-height primereact-dropdown-pr-0'
                                />
                            </div>
                        </div>
                    </div>

                    <NotificationPanel />

                    <div className="user-actions">
                        {user?.profile_picture ? (
                            <Avatar
                                image={`/dashboard/user/avatar/${user.profile_picture}`}
                                className="ml-auto mr-3"
                                size="large"
                                shape="circle"
                                onClick={(e) => userPanelRef.current.toggle(e)}
                            />
                        ) : (
                            <div className="deepsy-anonymous-avatar ml-auto mr-3" onClick={(e) => userPanelRef.current.toggle(e)}
                            ><span className='has-text-dgrad-deepsy'>{user?.first_name[0].toUpperCase()}{user?.last_name[0].toUpperCase()}</span></div>
                        )}

                        <OverlayPanel ref={userPanelRef}>
                            <div className="user-info">
                                <i className="fa-solid fa-user"></i> <strong className="is-capitalized">{user?.display_name || `${user?.first_name} ${user?.last_name || ''}`}</strong>
                            </div>

                            <hr />

                            {/* <span className='is-clickable mb-1' onClick={() => { console.log('clicked on "Paramètres"') }}>
                                <div className="user-option"><i className="fa-solid fa-cog"></i> Paramètres</div>
                            </span>

                            <span className='is-clickable mb-1' onClick={() => { console.log('clicked on "Gérer les crédits"') }}>
                                <div className="user-option"><i className="fa-solid fa-circle-plus"></i> Gérer les crédits</div>
                            </span> */}

                            {/* <hr /> */}

                            <span className='is-clickable mb-1' onClick={handleDisconnect}>
                                <div className="user-option"><i className="fa-solid fa-sign-out-alt"></i> Se déconnecter</div>
                            </span>

                        </OverlayPanel>
                    </div>
                </div>
            </header>
        </>
    );
};

export default Header;